import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RouteEnum } from '../../routes/routes';
import { useFooter } from '../../providers/FooterProvider';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import OrderCard from '../../components/cards/OrderCard';
import { Alert, Button, Flex, Segmented, Spin } from 'antd';
import { collect } from 'collect.js';
import { ClockCircleOutlined } from '@ant-design/icons';
import Layout from '../../components/layout/Layout';

export const Tab = {
  OngoingOrders: 'ongoing_orders',
  FutureOrders: 'future_orders',
  CompleteOrders: 'complete_orders',
};

const ActiveOrdersView = () => {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Views:ActiveOrders' });

  const { setFooterContent } = useFooter();
  const navigate = useNavigate();

  useEffect(() => {
    setFooterContent(
      <div className="flex justify-center py-md">
        <Button
          icon={<ClockCircleOutlined />}
          variant="outlined"
          color="default"
          onClick={() => navigate(RouteEnum.OrderHistory)}
        >
          {t('buttons.order_history')}
        </Button>
      </div>,
    );

    return () => setFooterContent(null);
  }, [i18n.language]);

  return <ActiveOrdersView.Tabs />;
};

export default ActiveOrdersView;

ActiveOrdersView.Tabs = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:ActiveOrders' });

  const { ongoingOrdersList, futureOrdersList, completeOrdersList, loading } = useOngoingOrders();
  const [activeTab, setActiveTab] = React.useState(Tab.OngoingOrders);
  const tabs = [
    {
      value: Tab.OngoingOrders,
      label: <ActiveOrdersView.TabLabel tab={Tab.OngoingOrders} ordersCount={ongoingOrdersList.length} />,
      children: <ActiveOrdersView.TabContent list={ongoingOrdersList} loading={loading} />,
    },
    {
      value: Tab.FutureOrders,
      label: <ActiveOrdersView.TabLabel tab={Tab.FutureOrders} ordersCount={futureOrdersList.length} />,
      children: futureOrdersList.length > 0 && (
        <ActiveOrdersView.TabContent list={futureOrdersList} loading={loading}>
          <Alert
            title={t('feedback.future_orders_alert')}
            type="warning"
            classNames={{
              title: 'font-semibold',
            }}
          />
        </ActiveOrdersView.TabContent>
      ),
    },
    {
      value: Tab.CompleteOrders,
      label: <ActiveOrdersView.TabLabel tab={Tab.CompleteOrders} ordersCount={completeOrdersList.length} />,
      children: <ActiveOrdersView.TabContent list={completeOrdersList} loading={loading} />,
    },
  ];

  return (
    <>
      <Segmented
        options={tabs}
        value={activeTab}
        onChange={setActiveTab}
        className="w-full segmented-multiline bg-success !rounded-none !p-none [&_.ant-segmented-item-selected]:!opacity-100 [&_.ant-segmented-item-selected]:!text-white [&_.ant-segmented-item-selected]:!border-b-white [&_.ant-segmented-item-selected]:bg-white/10 [&_.ant-segmented-thumb]:bg-white/10 [&_.ant-segmented-thumb]:!border-b-4 [&_.ant-segmented-thumb]:!border-b-white [&_.ant-segmented-thumb]:z-10 [&_.ant-segmented-thumb]:!rounded-none"
        classNames={{
          item: '!rounded-none !shadow-none !py-xs font-medium flex items-center !text-gray-200 border-b-4 border-b-success',
          label: '!px-none text-center w-full',
        }}
        block
        size="large"
        shape="default"
      />
      <Layout.Page className="space-y-md">{collect(tabs).firstWhere('value', activeTab).children}</Layout.Page>
    </>
  );
};

ActiveOrdersView.TabLabel = ({ tab, ordersCount }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:ActiveOrders' });

  return (
    <div className="flex flex-col justify-center items-center">
      <p className="text-md font-semibold">{ordersCount}</p>
      <p className="text-2xs">{t(`tabs.${tab}`)}</p>
    </div>
  );
};

ActiveOrdersView.TabContent = ({ list, loading, children }) => {
  return (
    <>
      {loading && (
        <Flex justify="center">
          <Spin className="m-auto" size="large" />
        </Flex>
      )}
      {list.length > 0 && (
        <>
          {children}
          {list.map((item) => (
            <OrderCard order={item} key={`order-${item.id}`} />
          ))}
        </>
      )}
    </>
  );
};
